import styles from './Breadcrumb.module.scss';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useTranslation } from '@hooks/useTranslation';
import { breadcrumbTitles } from './breadcrumbTitles';
import { LinksEnum } from '@specs/navigation';
import cn from 'classnames';
interface IProps {
  slugTitle?: string;
}
interface IBreadcrumb {
  href: string;
  text: string;
  showDots?: boolean;
  width: number;
}
type PathVariants = Partial<Record<(typeof LinksEnum)[keyof typeof LinksEnum], string[]>>;
export const Breadcrumb: FC<IProps> = ({
  slugTitle
}) => {
  const {
    pathname,
    query
  } = useRouter();
  const {
    translate: tr
  } = useTranslation();
  const containerRef = useRef<HTMLElement>(null);
  const pathVariants: PathVariants = {
    [LinksEnum.TOY]: ['explore', 'toys', '[slug]'],
    [LinksEnum.TOY_GENERATED_CODES]: ['explore', 'toys', '[slug]', 'generated_codes'],
    [LinksEnum.TOY_SELL]: ['explore', 'toys', '[slug]', 'sell'],
    [LinksEnum.TOY_SELLERS]: ['explore', 'toys', '[slug]', 'sellers'],
    [LinksEnum.TOY_TASK_COMPETITION]: ['explore', 'toys', '[slug]', 'competition'],
    [LinksEnum.TOY_TASK_ADS]: ['explore', 'toys', '[slug]', 'ads'],
    [LinksEnum.EXPLORE_ARTISTS]: ['explore', 'artists'],
    [LinksEnum.EXPLORE_COLLECTION]: ['explore', 'collections', '[slug]'],
    [LinksEnum.EXPLORE_COLLECTIONS]: ['explore', 'collections'],
    [LinksEnum.EXPLORE_TOYS]: ['explore', 'toys'],
    [LinksEnum.PAYMENT_GOLD]: ['gold'],
    [LinksEnum.PAYMENT_RUBY]: ['ruby'],
    [LinksEnum.PAYMENT_HISTORY]: ['history'],
    [LinksEnum.PAYMENT_WITHDRAW]: ['withdraw'],
    [LinksEnum.POST]: ['[slug]'],
    [LinksEnum.POST_CREATE]: ['create_post'],
    [LinksEnum.SETTINGS]: ['settings'],
    [LinksEnum.USER_TOY_DRAFT]: ['create_toy']
  };
  const slug = !slugTitle ? '' : Array.isArray(query.slug) ? query.slug[0] : query.slug || '';
  const measureItemWidth = ({
    text,
    withSeparator
  }): number => {
    if (typeof document === 'undefined') return 0; // Возвращаем 0 при SSR
    const tempDiv = document.createElement('div');
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.position = 'absolute';
    tempDiv.style.whiteSpace = 'nowrap';
    tempDiv.className = styles.item;
    const link = document.createElement('a');
    link.className = styles.link;
    link.textContent = text;
    if (withSeparator) {
      const separator = document.createElement('span');
      separator.className = styles.separator;
      separator.textContent = '/';
      tempDiv.appendChild(separator);
    }
    tempDiv.appendChild(link);
    document.body.appendChild(tempDiv);
    const width = tempDiv.offsetWidth;
    document.body.removeChild(tempDiv);
    return width;
  };
  const homeLink: IBreadcrumb = useMemo(() => ({
    href: '/',
    text: 'R.Toys',
    showDots: false,
    width: measureItemWidth({
      text: 'R.Toys',
      withSeparator: false
    })
  }), []);
  const [compressedItems, setCompressedItems] = useState<IBreadcrumb[]>([homeLink]);
  const compressedLinkWidth = useMemo(() => measureItemWidth({
    text: '...',
    withSeparator: true
  }), []);
  const generateBreadcrumbs = (): IBreadcrumb[] => {
    const pathData: string[] | undefined = pathVariants[pathname];
    const items = [homeLink];
    if (!pathData) return items;
    pathData.forEach((segment, index) => {
      let href: string;
      if (segment === '[slug]' && pathname.includes('toy/[slug]')) {
        href = `/toy/${slug}`;
      } else {
        href = '/' + pathData.slice(0, index + 1).map(s => s === '[slug]' ? slug || '' : s).join('/');
      }
      let text: string = segment;
      if (segment === '[slug]') {
        text = slugTitle || slug || '';
      } else if (segment in breadcrumbTitles) {
        text = tr({
          id: breadcrumbTitles[segment]
        });
      }
      const newItem: IBreadcrumb = {
        href,
        text,
        showDots: false,
        width: measureItemWidth({
          text,
          withSeparator: true
        })
      };
      items.push(newItem);
    });
    return items;
  };
  const baseBreadcrumbs: IBreadcrumb[] = useMemo(() => generateBreadcrumbs(), [pathname, slug, slugTitle, tr]);
  const compressItems = () => {
    if (!containerRef.current || baseBreadcrumbs.length < 2) return;
    const containerWidth = containerRef.current.offsetWidth - 32;

    // Проверяем, помещается ли всё целиком
    const totalWidth = baseBreadcrumbs.reduce((sum, item) => sum + item.width, 0);
    if (totalWidth <= containerWidth) {
      setCompressedItems(baseBreadcrumbs);
      return;
    }

    // Начинаем с максимального сжатия (все средние элементы сжаты)
    let items = baseBreadcrumbs.map((el, i, arr) => ({
      ...el,
      showDots: i !== 0 && i !== arr.length - 1,
      width: i !== 0 && i !== arr.length - 1 ? compressedLinkWidth : el.width
    }));
    // Считаем текущую ширину
    let currentWidth = items.reduce((sum, item) => sum + item.width, 0) + 8;

    // Пробуем разжимать элементы с конца
    for (let i = items.length - 2; i > 0; i--) {
      const originalWidth = baseBreadcrumbs[i].width;
      const widthWithoutCompression = currentWidth - compressedLinkWidth + originalWidth;

      // Если после разжатия этого элемента всё помещается
      if (widthWithoutCompression <= containerWidth) {
        items[i] = {
          ...baseBreadcrumbs[i],
          showDots: false
        };
        currentWidth = widthWithoutCompression;
      } else {
        break;
      }
    }
    setCompressedItems(items);
  };
  useEffect(() => {
    compressItems();
    const resizeObserver = new ResizeObserver(() => {
      compressItems();
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [baseBreadcrumbs]);
  return <nav className={styles.container} ref={containerRef} data-sentry-component="Breadcrumb" data-sentry-source-file="Breadcrumb.tsx">
            {compressedItems.map((item, i, arr) => {
      const isLast = i === arr.length - 1;
      const isFirst = i === 0;
      return <div className={styles.item} key={item.href}>
                        {!isFirst && <span className={styles.separator}>/</span>}
                        {isLast ? <span className={cn(styles.link, styles.disabled)}>
                                {item.showDots ? '...' : item.text}
                            </span> : <Link href={item.href} className={styles.link}>
                                  {item.showDots ? '...' : item.text}
                            </Link>}
                    </div>;
    })}
        </nav>;
};